import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Building, Users, Activity, Phone, Mail, Calendar, Download } from 'lucide-react';

const HealthZoneDetailsModal = ({ healthZone, isOpen, onClose }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Nettoyer les ressources lorsque le composant est démonté
  useEffect(() => {
    return () => {
      // Nettoyage effectué lors du démontage
    };
  }, []);

  // Générer le PDF
  const generateDetailedPDF = useCallback(async () => {
    setIsGeneratingPDF(true);
    let blobUrl = null;
    
    try {
      // Appeler l'API backend pour générer le PDF avec Puppeteer
      const response = await fetch('https://backendhealthbridge.onrender.com/api/health-zones/generate-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          healthZoneId: healthZone.id,
          healthZoneData: healthZone
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }

      // Créer un blob à partir de la réponse
      const blob = await response.blob();
      
      // Vérifier que le blob est bien un PDF
      if (blob.type !== 'application/pdf') {
        throw new Error('Le fichier téléchargé n\'est pas un PDF valide');
      }
      
      // Créer un lien de téléchargement avec timestamp unique
      blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `rapport-zone-sante-${healthZone.code}-${Date.now()}.pdf`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer immédiatement
      document.body.removeChild(link);
      
      // Afficher un message de succès
      alert('PDF généré avec succès! Le téléchargement va démarrer.');
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF: ' + error.message);
    } finally {
      // Nettoyer les URLs d'objet blob
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
      setIsGeneratingPDF(false);
    }
  }, [healthZone]);

  // Empêcher la propagation de l'événement
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Fonctionnelle':
        return 'bg-green-100 text-green-800';
      case 'Partiellement fonctionnelle':
        return 'bg-yellow-100 text-yellow-800';
      case 'Non fonctionnelle':
        return 'bg-red-100 text-red-800';
      case 'En construction':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen || !healthZone) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={handleModalClick}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Détails de la Zone de Santé</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={generateDetailedPDF}
              disabled={isGeneratingPDF}
              className={`inline-flex items-center px-4 py-2 bg-primary-600 ${
                isGeneratingPDF ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-700'
              } text-white text-sm font-medium rounded-lg transition-colors duration-200`}
            >
              {isGeneratingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Génération...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Générer PDF
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* En-tête */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{healthZone.name}</h3>
                <p className="text-lg text-gray-600 mb-2">Code: {healthZone.code}</p>
                <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(healthZone.status)}`}>
                  {healthZone.status}
                </div>
              </div>
            </div>
          </div>

          {/* Informations de base */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Localisation
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span><strong>Province:</strong> {healthZone.province}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Building className="h-4 w-4 mr-2" />
                  <span><strong>Territoire:</strong> {healthZone.territory}</span>
                </div>
                {healthZone.sector && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Building className="h-4 w-4 mr-2" />
                    <span><strong>Secteur:</strong> {healthZone.sector}</span>
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-600">
                  <Building className="h-4 w-4 mr-2" />
                  <span><strong>Chef-lieu:</strong> {healthZone.chiefTown}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="h-4 w-4 mr-2" />
                  <span><strong>Population:</strong> {healthZone.population?.toLocaleString() || 'Non spécifiée'}</span>
                </div>
                {healthZone.area && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span><strong>Superficie:</strong> {healthZone.area} km²</span>
                  </div>
                )}
                {healthZone.coordinates && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span><strong>Coordonnées:</strong> {healthZone.coordinates.latitude}, {healthZone.coordinates.longitude}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Infrastructures de santé */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Infrastructures de santé
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{healthZone.healthCenters || 0}</div>
                <div className="text-sm text-gray-600">Centres de santé</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{healthZone.hospitals || 0}</div>
                <div className="text-sm text-gray-600">Hôpitaux</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-purple-600">{healthZone.healthPosts || 0}</div>
                <div className="text-sm text-gray-600">Postes de santé</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {(healthZone.healthCenters || 0) + (healthZone.hospitals || 0) + (healthZone.healthPosts || 0)}
                </div>
                <div className="text-sm text-gray-600">Total infrastructures</div>
              </div>
            </div>
            {healthZone.budget && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center text-sm text-gray-600">
                  <span><strong>Budget annuel:</strong> ${healthZone.budget.toLocaleString()} USD</span>
                </div>
              </div>
            )}
          </div>

          {/* Personnel de santé */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Personnel de santé
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{healthZone.doctors || 0}</div>
                <div className="text-sm text-gray-600">Médecins</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{healthZone.nurses || 0}</div>
                <div className="text-sm text-gray-600">Infirmiers</div>
              </div>
              <div className="bg-pink-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-pink-600">{healthZone.midwives || 0}</div>
                <div className="text-sm text-gray-600">Sages-femmes</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{healthZone.healthWorkers || 0}</div>
                <div className="text-sm text-gray-600">Agents de santé</div>
              </div>
            </div>
          </div>

          {/* Contact */}
          {(healthZone.contactPerson || healthZone.contactPhone || healthZone.contactEmail) && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <Phone className="h-5 w-5 mr-2" />
                Contact
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {healthZone.contactPerson && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="h-4 w-4 mr-2" />
                    <span><strong>Personne de contact:</strong> {healthZone.contactPerson}</span>
                  </div>
                )}
                {healthZone.contactPhone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 mr-2" />
                    <span><strong>Téléphone:</strong> {healthZone.contactPhone}</span>
                  </div>
                )}
                {healthZone.contactEmail && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="h-4 w-4 mr-2" />
                    <span><strong>Email:</strong> {healthZone.contactEmail}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Évaluations */}
          {(healthZone.lastEvaluation || healthZone.nextEvaluation) && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Évaluations
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {healthZone.lastEvaluation && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span><strong>Dernière évaluation:</strong> {new Date(healthZone.lastEvaluation).toLocaleDateString()}</span>
                  </div>
                )}
                {healthZone.nextEvaluation && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span><strong>Prochaine évaluation:</strong> {new Date(healthZone.nextEvaluation).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {healthZone.notes && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">Notes et observations</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{healthZone.notes}</p>
              </div>
            </div>
          )}

          {/* Boutons */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthZoneDetailsModal;
